/**
 * File List View Component
 * Displays files and folders in a traditional file explorer list format
 */

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material'
import {
  Folder,
  Image,
  VideoFile,
  AudioFile,
  Description,
  InsertDriveFile,
  Star,
  StarBorder,
  MoreVert,
} from '@mui/icons-material'
import { formatFileSize, getFileTypeIcon } from '@api/types/files'
import type { UserFileDto, MediaFolderDto } from '@api/types/files'

interface FileListViewProps {
  folders: MediaFolderDto[]
  files: UserFileDto[]
  showFolders: boolean
  onFolderClick: (folder: MediaFolderDto) => void
  onFolderMenuClick: (event: React.MouseEvent<HTMLElement>, folder: MediaFolderDto) => void
  onFileClick: (file: UserFileDto) => void
  onFileFavoriteToggle: (file: UserFileDto) => void
  onFileMenuClick: (event: React.MouseEvent<HTMLElement>, file: UserFileDto) => void
}

function FileTypeIcon({ mimeType }: { mimeType: string }) {
  const type = getFileTypeIcon(mimeType)
  const iconProps = { sx: { fontSize: 24, color: 'text.secondary' } }

  switch (type) {
    case 'image':
      return <Image {...iconProps} />
    case 'video':
      return <VideoFile {...iconProps} />
    case 'audio':
      return <AudioFile {...iconProps} />
    case 'document':
      return <Description {...iconProps} />
    default:
      return <InsertDriveFile {...iconProps} />
  }
}

export function FileListView({
  folders,
  files,
  showFolders,
  onFolderClick,
  onFolderMenuClick,
  onFileClick,
  onFileFavoriteToggle,
  onFileMenuClick,
}: FileListViewProps) {
  const hasContent = (showFolders && folders.length > 0) || files.length > 0

  if (!hasContent) return null

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell width={60}></TableCell>
            <TableCell>Name</TableCell>
            <TableCell width={100} align="right">Size</TableCell>
            <TableCell width={140}>Date</TableCell>
            <TableCell width={100} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Folders */}
          {showFolders && folders.map((folder) => (
            <TableRow
              key={`folder-${folder.id}`}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onFolderClick(folder)}
            >
              <TableCell>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Folder sx={{ fontSize: 32, color: folder.color || '#FFC107' }} />
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {folder.name || 'Unnamed Folder'}
                </Typography>
                {folder.description && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                    {folder.description}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {folder.fileCount} file{folder.fileCount !== 1 ? 's' : ''}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {new Date(folder.updatedDate).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    onFolderMenuClick(e, folder)
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}

          {/* Files */}
          {files.map((file) => {
            const isImage = file.mimeType?.startsWith('image/')
            const displayName = file.displayName || file.fileName

            return (
              <TableRow
                key={`file-${file.id}`}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onFileClick(file)}
              >
                <TableCell>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'grey.100',
                    }}
                  >
                    {isImage && (file.thumbnailUrl || file.fileUrl) ? (
                      <Avatar
                        variant="rounded"
                        src={file.thumbnailUrl || file.fileUrl}
                        alt={displayName}
                        sx={{ width: 40, height: 40 }}
                      />
                    ) : (
                      <FileTypeIcon mimeType={file.mimeType || ''} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                      {displayName}
                    </Typography>
                    {file.isFavorite && (
                      <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                    )}
                  </Box>
                  {file.folderName && (
                    <Typography variant="caption" color="text.secondary">
                      in {file.folderName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.fileSize)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(file.createdDate).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Tooltip title={file.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          onFileFavoriteToggle(file)
                        }}
                      >
                        {file.isFavorite ? (
                          <Star fontSize="small" sx={{ color: 'warning.main' }} />
                        ) : (
                          <StarBorder fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onFileMenuClick(e, file)
                      }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
